import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import type { IncompleteCostResponse } from '../value.api';
import { formatEur } from '../format';

interface IncompleteCostCardProps {
  data: IncompleteCostResponse;
  savingsPath: string;
}

export function IncompleteCostCard({ data, savingsPath }: IncompleteCostCardProps) {
  const { t } = useTranslation();

  if (data.totalMissingFields === 0) return null;

  const riskColor = data.totalEstimatedCost > 5000 ? 'var(--destructive)' : 'var(--warning, #E67E22)';
  const riskBg = data.totalEstimatedCost > 5000 ? 'var(--destructive-light)' : 'var(--warning-light, #FEF3E2)';

  return (
    <div
      data-testid="incomplete-cost-card"
      className="rounded-xl px-5 py-4"
      style={{
        backgroundColor: 'var(--background)',
        border: '1px solid var(--border-soft)',
        borderLeft: `3px solid ${riskColor}`,
        boxShadow: 'var(--card-shadow)',
      }}
    >
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs" style={{ color: 'var(--foreground-muted)' }}>
            {t('incompleteCost.title')}
          </p>
          <p className="mt-1 text-2xl font-bold" style={{ color: riskColor }}>
            {formatEur(data.totalEstimatedCost)}
          </p>
          <p className="mt-1 text-xs" style={{ color: 'var(--foreground-muted)' }}>
            {t('incompleteCost.productsAtRisk', { count: data.productsAtRisk })}
            {' · '}
            {t('incompleteCost.missingFields', { count: data.totalMissingFields })}
          </p>
          {data.timeToMarketImpact.productsDelayed > 0 && (
            <p className="mt-1 text-xs font-medium" style={{ color: riskColor }}>
              <span className="mdi mdi-alert-circle-outline mr-1" style={{ fontSize: '12px' }} />
              {t('incompleteCost.timeToMarket', { count: data.timeToMarketImpact.productsDelayed })}
            </p>
          )}
        </div>
        <div className="flex flex-col items-end gap-1">
          <div
            className="flex h-9 w-9 items-center justify-center rounded-lg"
            style={{ backgroundColor: riskBg }}
          >
            <span className="mdi mdi-alert-circle-outline" style={{ fontSize: '20px', color: riskColor }} />
          </div>
        </div>
      </div>
      <div className="mt-3">
        <Link
          to={savingsPath}
          className="text-xs font-medium"
          style={{ color: 'var(--primary)' }}
        >
          {t('incompleteCost.viewDetails')} →
        </Link>
      </div>
    </div>
  );
}
